import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';

import { MessageSnackbar } from '@/__core/helpers/components';

import { RequestMaterialItemOption, RequestMaterialLine, RequestMaterialUomOption } from '../types';

interface PurchaseOrderItem {
  lineId: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  requestedQty: number;
  purchaseQty: number;
  uomId: string;
  uomName: string;
}

interface PurchaseOrderDraft {
  supplierId: string;
  supplierName: string;
  requiredBy: string | null;
  items: PurchaseOrderItem[];
}

interface CreatePurchaseOrderModalProps {
  open: boolean;
  onClose: () => void;
  selectedLines: RequestMaterialLine[];
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  defaultRequiredBy?: string | null;
  onConfirm: (orders: PurchaseOrderDraft[]) => Promise<void>;
  loading?: boolean;
}

export default function CreatePurchaseOrderModal({
  open,
  onClose,
  selectedLines,
  itemOptions,
  uomOptions,
  defaultRequiredBy,
  onConfirm,
  loading = false,
}: CreatePurchaseOrderModalProps) {
  const [orders, setOrders] = React.useState<PurchaseOrderDraft[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const itemLines = selectedLines.filter((line) => line.line_type === 'item' && line.item_id);

    const groupedBySupplier = new Map<string, { supplierId: string; items: PurchaseOrderItem[] }>();

    itemLines.forEach((line) => {
      const matchedItem = itemOptions.find((item) => item.id === line.item_id);
      const supplierName = matchedItem?.supplier_name?.trim() || 'Chưa có nhà cung cấp';
      const supplierId = matchedItem?.supplier_id || '';

      const uom = uomOptions.find((u) => u.id === line.uom_id);
      const requestedQty = Number(line.requested_qty) || 0;

      const item: PurchaseOrderItem = {
        lineId: line.id,
        itemId: line.item_id || '',
        itemCode: line.item_code || matchedItem?.code || '',
        itemName: line.item_name || matchedItem?.name || '',
        requestedQty,
        purchaseQty: requestedQty,
        uomId: line.uom_id || '',
        uomName: uom?.name || line.uom_name || '',
      };

      if (!groupedBySupplier.has(supplierName)) {
        groupedBySupplier.set(supplierName, { supplierId, items: [] });
      }
      groupedBySupplier.get(supplierName)!.items.push(item);
    });

    const drafts: PurchaseOrderDraft[] = Array.from(groupedBySupplier.entries()).map(([supplierName, { supplierId, items }]) => ({
      supplierId,
      supplierName,
      requiredBy: defaultRequiredBy || null,
      items,
    }));

    setOrders(drafts);
  }, [open, selectedLines, itemOptions, uomOptions, defaultRequiredBy]);

  const handleQtyChange = (orderIndex: number, itemIndex: number, value: string) => {
    const raw = parseFloat(value);
    setOrders((prev) => {
      const next = [...prev];
      const order = { ...next[orderIndex], items: [...next[orderIndex].items] };
      const item = { ...order.items[itemIndex] };
      item.purchaseQty = isNaN(raw) ? item.requestedQty : Math.max(item.requestedQty, Math.round(raw * 100) / 100);
      order.items[itemIndex] = item;
      next[orderIndex] = order;
      return next;
    });
  };

  const handleRequiredByChange = (orderIndex: number, date: Date | null) => {
    setOrders((prev) => {
      const next = [...prev];
      next[orderIndex] = {
        ...next[orderIndex],
        requiredBy: date ? date.toISOString().slice(0, 10) : null,
      };
      return next;
    });
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await onConfirm(orders);
      setSuccessMessage('Phiếu mua hàng đã được tạo thành công!');

      // Close dialog after showing success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail
        || error?.message
        || 'Có lỗi xảy ra khi tạo phiếu mua hàng';
      setErrorMessage(errorMsg);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart sx={{ color: '#2563eb', fontSize: '1.25rem' }} />
          <Typography variant='h6' sx={{ fontWeight: 700, color: '#111827' }}>
            Tạo phiếu mua hàng
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', mt: 0.5 }}>
          {orders.length} phiếu mua hàng cho {orders.reduce((sum, o) => sum + o.items.length, 0)} vật tư được chọn
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {errorMessage && (
          <Box sx={{ p: 2, backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 0, color: '#dc2626' }}>
            <Typography variant="body2">{errorMessage}</Typography>
          </Box>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
          {orders.map((order, orderIndex) => (
            <Box key={order.supplierName}>
              {orderIndex > 0 && <Divider sx={{ borderColor: '#e5e7eb', borderWidth: 2 }} />}
              <Box sx={{ p: 3 }}>
                {/* Supplier header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
                      Nhà cung cấp
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                      {order.supplierName}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
                      Ngày cần có
                    </Typography>
                    <DatePicker
                      value={order.requiredBy ? new Date(order.requiredBy) : null}
                      onChange={(date) => handleRequiredByChange(orderIndex, date)}
                      format='dd/MM/yyyy'
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Chọn ngày cần có',
                          sx: { minWidth: 180 },
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Items table */}
                <Box
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', width: '130px' }}>Mã vật tư</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', width: '130px' }}>
                          Số lượng
                          <Typography component='span' sx={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400, ml: 0.5 }}>
                            (YC)
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#374151', width: '110px' }}>ĐVT</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item, itemIndex) => (
                        <TableRow key={item.lineId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ color: '#6b7280', fontSize: '0.8rem' }}>{itemIndex + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', fontFamily: '"Roboto Mono", monospace' }}>
                              {item.itemCode || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>{item.itemName || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              type='number'
                              value={item.purchaseQty}
                              onChange={(e) => handleQtyChange(orderIndex, itemIndex, e.target.value)}
                              inputProps={{ min: item.requestedQty, step: 0.01 }}
                              helperText={`YC: ${item.requestedQty}`}
                              FormHelperTextProps={{ sx: { fontSize: '0.7rem', mx: 0 } }}
                              sx={{
                                width: '110px',
                                '& .MuiInputBase-input': { fontSize: '0.875rem' },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>{item.uomName || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          ))}

          {orders.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#9ca3af' }}>Không có vật tư nào được chọn.</Typography>
            </Box>
          )}
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant='outlined'
          onClick={onClose}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Hủy
        </Button>
        <Button
          variant='contained'
          disabled={orders.length === 0 || loading}
          onClick={handleConfirm}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none', backgroundColor: '#2563eb', '&:hover': { backgroundColor: '#1d4ed8' } }}
        >
          {loading ? 'Đang tạo...' : 'Xác nhận tạo phiếu'}
        </Button>
      </DialogActions>

      {/* Success Snackbar */}
      <MessageSnackbar
        open={!!successMessage && !errorMessage}
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
      />

      {/* Error Snackbar */}
      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity="error"
        onClose={() => setErrorMessage(null)}
      />
    </Dialog>
  );
}
